import { useCallback, useState } from 'react';
import { ScrollView, View } from 'react-native';
import i18n from 'i18n-js';
import { useSelector } from 'react-redux';
import { getNCFileFromVersions } from '@helpers';
import { scale } from '@components/frame';
import { useFetch } from '@api/hooks';
import { NcFile } from '@api/domain';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getNCFileById } from '@reducers/manufacture';
import { buildVersionLabel } from '@components/ncFileVersions/helpers/buildVersionLabel';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { selectSelectedItem } from '@reducers/machines';
import { Machine } from '@interfaces/machines';
import { ProductionBatchStatistics } from '@interfaces/search';
import { selectSingleBatch } from '@reducers/batch';
import { NC_FILE_BLOCK_MAX_HEIGHT, NC_FILE_LARGE_MODE_THRESHOLD } from '@v2/pages/machines/modules/now/components/Overview/constants';
import { NcFileBlockProps } from '@v2/pages/machines/modules/now/components/Overview/components/NCFileBlock';
import { useBatchLoaded } from '@v2/pages/machines/modules/now/components/Overview/hooks/useBatchLoaded';

export interface UseManageNCFileBlockParams extends NcFileBlockProps {}

export const useManageNCFileBlock = ({ C, largeMode, largeHeight }: UseManageNCFileBlockParams) => {
  const dispatch = useAppDispatch();
  const batch: ProductionBatchStatistics = useSelector(selectSingleBatch);
  const selectedMachine: Machine = useSelector(selectSelectedItem);
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const batchLoaded = useBatchLoaded();

  const machineStatus = selectedMachine?.machineStatus;
  const ncFileId = machineStatus?.ncFileId;
  const ncFileName = machineStatus?.ncFile;
  const ncFileStatus = machineStatus?.ncFileStatus;

  const batchId = batch?.productionBatchId;
  const batchNumber = batch?.batchNumber;

  const num = machineStatus?.ncFileNumber;
  const liveVersionLabel = num ? `${num} (${i18n.t(machineStatus?.ncFileIsFavorite ? 'favorite' : 'latest')})` : '';

  // Some machines report no ncFileNumber in their live status even when the NC file has versions.
  // Fall back to the file's latest/favorite version (the same source the NC file details screen uses)
  // so the Version header doesn't vanish. The label is derived only when the fetched file matches the
  // current ncFileId, so a slow/failed fetch on a previous file can never label the current one.
  const { data: fallbackNcFile } = useFetch<NcFile | null>({
    fetchFn: (signal) =>
      !liveVersionLabel && ncFileId ? dispatch(getNCFileById(ncFileId, undefined, signal)).then((file) => file ?? null) : Promise.resolve(null),
    deps: [ncFileId, liveVersionLabel],
  });

  const fallbackVersionLabel =
    fallbackNcFile && fallbackNcFile.ncFileId === ncFileId ? buildVersionLabel(getNCFileFromVersions(fallbackNcFile.versions)) : '';

  const versionNumber = liveVersionLabel || fallbackVersionLabel;

  const handleNCFileNavigation = useCallback(
    (levelSubScreen: NCFileLevelSubScreen) => {
      navigateToMainDetailsScreen({
        mainScreen: BatchDetailsScreenName,
        subScreen: BatchDetailsScreen.NC_FILE,
        levelSubScreen,
        params: {
          perspective: Perspective.BATCH,
          batchId,
          batchNumber,
          ncFileStatus,
        },
      });
    },
    [navigateToMainDetailsScreen, batchId, batchNumber, ncFileStatus]
  );

  const [shouldShowOverlay, setShouldShowOverlay] = useState(false);

  const baseColor = C.scheme === 'dark' ? '0, 0, 0' : '242, 242, 247';

  const NcFileContentWrapper = largeMode ? ScrollView : View;

  const hasLargeHeight = largeMode && typeof largeHeight === 'number' && largeHeight > 0;

  const tourStyle = hasLargeHeight
    ? { height: largeHeight }
    : { flex: 1, minHeight: scale(NC_FILE_LARGE_MODE_THRESHOLD), maxHeight: scale(NC_FILE_BLOCK_MAX_HEIGHT) };

  return {
    selectedMachine,
    ncFileId,
    ncFileName,
    versionNumber,
    handleNCFileNavigation,
    shouldShowOverlay,
    setShouldShowOverlay,
    baseColor,
    NcFileContentWrapper,
    hasLargeHeight,
    tourStyle,
    batchLoaded,
  };
};
